import { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { Writer } from '../Blog.types';
import { ShowToast } from '../../../services/toast';
import { getBlogErrorMessage } from '../blog-errors';

type GetWritersParams = {
  page?: number;
  limit?: number;
  keyword?: string;
  sort?: keyof Writer;
  sortAsc?: boolean;
};

type WriterContextType = {
  writers: Writer[];
  total: number;
  loading: boolean;
  fetchWriters: (params?: GetWritersParams) => Promise<void>;
  createWriter: (writer: Writer) => Promise<void>;
  updateWriter: (id: string, writer: Writer) => Promise<void>;
  deleteWriter: (id: string) => Promise<void>;
};

const WriterContext = createContext<WriterContextType | undefined>(undefined);

export function WriterProvider({ children }: { children: React.ReactNode }) {
  const [writers, setWriters] = useState<Writer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchWriters = useCallback(async (params?: GetWritersParams) => {
    try {
      setLoading(true);
      const response = await api.getWriters({
        page: params?.page,
        limit: params?.limit,
        keyword: params?.keyword,
      });
      setWriters(response.writers);
      setTotal(response.total);
    } catch (error: unknown) {
      ShowToast({
        type: 'error',
        message: getBlogErrorMessage(error, 'Failed to fetch contributors'),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const createWriter = useCallback(
    async (writer: Writer) => {
      try {
        setLoading(true);
        await api.createWriter(writer);
        await fetchWriters();
        ShowToast({
          type: 'success',
          message: 'Contributor created successfully',
        });
      } catch (error: unknown) {
        ShowToast({
          type: 'error',
          message: getBlogErrorMessage(error, 'Failed to create contributor'),
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchWriters]
  );

  const updateWriter = useCallback(
    async (id: string, writer: Writer) => {
      try {
        setLoading(true);
        await api.updateWriter(id, writer);
        await fetchWriters();
        ShowToast({
          type: 'success',
          message: 'Contributor updated successfully',
        });
      } catch (error: unknown) {
        ShowToast({
          type: 'error',
          message: getBlogErrorMessage(error, 'Failed to update contributor'),
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchWriters]
  );

  const deleteWriter = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        await api.deleteWriter(id);
        await fetchWriters();
        ShowToast({
          type: 'success',
          message: 'Contributor deactivated successfully',
        });
      } catch (error: unknown) {
        ShowToast({
          type: 'error',
          message: getBlogErrorMessage(error, 'Failed to deactivate contributor'),
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchWriters]
  );

  useEffect(() => {
    fetchWriters();
  }, []);

  return (
    <WriterContext.Provider
      value={{
        writers,
        total,
        loading,
        fetchWriters,
        createWriter,
        updateWriter,
        deleteWriter,
      }}
    >
      {children}
    </WriterContext.Provider>
  );
}

export function useWriters() {
  const context = useContext(WriterContext);
  if (context === undefined) {
    throw new Error('useWriters must be used within a WriterProvider');
  }
  return context;
}
